// Die eigene Position an den Server (task-013, ADR 0027). Der Avatar läuft weiter im Client; der
// Server braucht seine Position trotzdem, denn er misst daran die Nähe jeder Interaktion und
// prüft, ob die Bewegung überhaupt möglich war. Gemeldet wird als `move`, höchstens zehnmal je
// Sekunde und nur bei Änderung (docs/architektur-paket-2.md, „Protokoll v2“).
//
// Hält der Server eine Meldung für unmöglich, schickt er dem Spieler dessen letzte gültige
// Position als `players`-Nachricht mit dem eigenen Eintrag zurück; der Client springt dorthin.
//
// Reine Logik ohne DOM.
import { PROTOCOL_VERSION, TICK_RATE, type MoveMessage, type PlayerPose, type PlayerState, type PlayersMessage } from '@bauernhof/shared';

/** Abstand zweier Positionsmeldungen: zehnmal je Sekunde. */
export const MELDE_ABSTAND_MS = 100;

/**
 * Die Zeit der Simulation in Millisekunden – die Zeit, in der `faellig` rechnen soll. Mit der Uhrzeit
 * des Bildes kam die Meldung mal nach drei, mal nach vier Ticks, weil sechs Bilder nicht ganz 100 ms
 * sind; beim Mitspieler sah das wie Stocken aus (task-016, ADR 0032). Drei Ticks sind genau 100 ms.
 */
export function simZeitMs(tick: number): number {
  return (tick * 1000) / TICK_RATE;
}

/** Kleinere Abweichungen gelten nicht als Bewegung (Blöcke bzw. Bogenmaß). */
const EPSILON = 1e-4;

/** Was von einem Avatar gemeldet wird. */
export type MeldePose = Pick<PlayerState, 'pos' | 'yaw' | 'pitch' | 'flying'>;

export interface Positionsmelder {
  /**
   * Die fällige Meldung, oder null: nichts geändert, oder die letzte ist keine `MELDE_ABSTAND_MS`
   * her. `sofort` übergeht den Abstand – vor einer Interaktion soll der Server wissen, wo man
   * steht. Erst `gesendet` macht eine Meldung zur gemeldeten.
   */
  faellig(player: MeldePose, jetzt: number, sofort?: boolean): MoveMessage | null;
  /** Die Meldung ist hinausgegangen. */
  gesendet(msg: MoveMessage, jetzt: number): void;
  /** Der Server hat die Position gesetzt (Begrüßung, Rücksetzen); sie gilt als gemeldet. */
  uebernimm(player: MeldePose, jetzt: number): void;
}

export function createPositionsmelder(): Positionsmelder {
  let letzte: MoveMessage | null = null;
  let letzteZeit = Number.NEGATIVE_INFINITY;

  return {
    faellig(player, jetzt, sofort = false) {
      if (letzte !== null && !geaendert(letzte, player)) return null;
      if (!sofort && jetzt - letzteZeit < MELDE_ABSTAND_MS) return null;
      return alsNachricht(player);
    },
    gesendet(msg, jetzt) {
      letzte = { ...msg, pos: { ...msg.pos } };
      letzteZeit = jetzt;
    },
    uebernimm(player, jetzt) {
      letzte = alsNachricht(player);
      letzteZeit = jetzt;
    },
  };
}

/**
 * Der eigene Eintrag in einer `players`-Nachricht, oder null. Den schickt der Server nur, um eine
 * unmögliche Meldung zurückzunehmen; im Takt stehen in `players` nur die anderen (task-016).
 */
export function ruecksetzungFuer(msg: PlayersMessage, eigeneId: number): PlayerPose | null {
  const eintrag = msg.list.find((pose) => pose.id === eigeneId);
  return eintrag === undefined ? null : { ...eintrag, pos: { ...eintrag.pos } };
}

function alsNachricht(player: MeldePose): MoveMessage {
  return {
    v: PROTOCOL_VERSION,
    t: 'move',
    pos: { x: player.pos.x, y: player.pos.y, z: player.pos.z },
    yaw: player.yaw,
    pitch: player.pitch,
    flying: player.flying,
  };
}

function geaendert(letzte: MoveMessage, player: MeldePose): boolean {
  return (
    Math.abs(letzte.pos.x - player.pos.x) > EPSILON ||
    Math.abs(letzte.pos.y - player.pos.y) > EPSILON ||
    Math.abs(letzte.pos.z - player.pos.z) > EPSILON ||
    Math.abs(letzte.yaw - player.yaw) > EPSILON ||
    Math.abs(letzte.pitch - player.pitch) > EPSILON ||
    letzte.flying !== player.flying
  );
}
